/**
 * QC presentation helpers — number parsing/formatting, the verdict / lot-statut → (tone + glyph + label
 * key) maps (color + glyph + text, never color alone — daltonisme + médico-légal), and the
 * module-absent detector for the cloud-only degradation path.
 */
import type { ApiError } from "@/api";
import type { BadgeTone } from "@/ui";
import type { LotStatut, Verdict } from "./types";

/** Parse a user-typed number (FR decimal comma tolerated). Returns null on empty/NaN. */
export function parseNum(raw: string): number | null {
  const s = raw.trim().replace(",", ".");
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Format a number for display, LTR mono. Keeps up to 3 decimals, trims trailing zeros. */
export function fmtNum(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 3 }).format(n);
}

/** Signed z-score, 2 decimals (e.g. "+1.83", "−0.40"). */
export function fmtZ(z: number | null | undefined): string {
  if (z == null || !Number.isFinite(z)) return "—";
  const v = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: "exceptZero",
  }).format(z);
  // Intl uses a hyphen-minus; swap for the typographic minus to match the chart axis.
  return v.replace("-", "−");
}

/** A short, locale-aware day/time label for the chart x-axis + tables. */
export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export interface Visual {
  tone: BadgeTone;
  glyph: string;
  labelKey: string;
}

/** Verdict chip visual — rejete = critical (hold), accepte = normal, alerte = high, en_cours = neutral. */
export function verdictVisual(verdict: Verdict): Visual {
  switch (verdict) {
    case "rejete":
      return { tone: "critical", glyph: "◆", labelKey: "qc.verdict.rejete" };
    case "alerte":
      return { tone: "high", glyph: "↑", labelKey: "qc.verdict.alerte" };
    case "accepte":
      return { tone: "normal", glyph: "●", labelKey: "qc.verdict.accepte" };
    case "en_cours":
      return { tone: "neutral", glyph: "○", labelKey: "qc.verdict.en_cours" };
    default:
      return { tone: "neutral", glyph: "○", labelKey: "qc.verdict.unknown" };
  }
}

/** Lot statut chip visual. */
export function lotStatutVisual(statut: LotStatut): Visual {
  switch (statut) {
    case "valide":
      return { tone: "success", glyph: "✓", labelKey: "qc.lot.statut.valide" };
    case "brouillon":
      return { tone: "neutral", glyph: "✎", labelKey: "qc.lot.statut.brouillon" };
    case "expire":
      return { tone: "danger", glyph: "⊘", labelKey: "qc.lot.statut.expire" };
    default:
      return { tone: "neutral", glyph: "•", labelKey: "qc.lot.statut.unknown" };
  }
}

/**
 * QC is a LOCAL-deploy module, but a build may not have its routes mounted (or the install predates 08).
 * When that happens a probe 404s (NOT_FOUND / 501 NOT_IMPLEMENTED). The screens then render a clean
 * "non disponible sur cette installation" panel instead of a hard error. Auth/forbidden are NOT this —
 * those are the sans-droit path (handled by ForbiddenState).
 */
export function isModuleAbsent(error: ApiError | null | undefined): boolean {
  if (!error) return false;
  if (error.isUnauthenticated || error.isForbidden) return false;
  return error.httpStatus === 404 || error.httpStatus === 501 || error.code === "NOT_IMPLEMENTED";
}

/** Niveau → i18n label key. */
export function niveauKey(niveau: string): string {
  return `qc.niveau.${niveau}`;
}
